import styled, { css } from 'styled-components'
import { colors, fieldLabel, fontScope, radius, visuallyHidden } from '../../../components/SalesReport/tokens'
import { ChipTone } from '../report'

const MOBILE = '@media (max-width: 767px)'

export const Content = styled.section`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;

  ${MOBILE} {
    gap: 16px;
  }
`

export const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;

  ${MOBILE} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
`

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid ${colors.border};
  border-radius: ${radius.card};
  background: ${colors.white};
`

export const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 12px;

  ${MOBILE} {
    padding: 16px 16px 8px;
  }
`

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${colors.ink950};
`

export const CardSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 19px;
  color: ${colors.ink500};
`

export const Tools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export const ChartBox = styled.div<{ $height: number }>`
  position: relative;
  height: ${({ $height }) => $height}px;
  padding: 0 24px 20px;

  ${MOBILE} {
    height: ${({ $height }) => Math.round($height * 0.8)}px;
    padding: 0 12px 16px;
  }
`

export const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`

export const Band = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  padding: 20px 24px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.card};
  background: ${colors.white};

  ${MOBILE} {
    padding: 16px;
  }
`

export const Label = styled.span`
  ${fieldLabel}
`

const TONE_TEXT = { positive: colors.positive, warning: colors.warningText, danger: colors.dangerText }

export const Percent = styled.span<{ $tone: keyof typeof TONE_TEXT | 'muted' }>`
  font-size: 28px;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: -0.6px;
  color: ${({ $tone }) => ($tone === 'muted' ? colors.ink500 : TONE_TEXT[$tone])};
`

export const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  line-height: 19px;
  color: ${colors.ink600};
`

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const CHIP_TONES: Record<ChipTone, ReturnType<typeof css>> = {
  good: css`
    background: ${colors.positiveBg};
    color: ${colors.positive};
  `,
  crit: css`
    background: ${colors.dangerBg};
    color: ${colors.dangerText};
  `,
  warn: css`
    background: ${colors.warningBg};
    color: ${colors.warningText};
  `,
  info: css`
    background: ${colors.accent};
    color: ${colors.ink700};
  `,
}

export const Chip = styled.span<{ $tone: ChipTone }>`
  padding: 4px 12px;
  border-radius: ${radius.pill};
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  ${({ $tone }) => CHIP_TONES[$tone]}
`

export const LiveBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: ${radius.card};
  background: ${colors.dangerBg};
  font-size: 13px;
  font-weight: 700;
  color: ${colors.dangerText};
`

export const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: ${radius.pill};
  background: ${colors.primary};
  animation: access-live-pulse 1.4s ease-in-out infinite;

  @keyframes access-live-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
`

export const OriginList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 4px 24px 20px;
  list-style: none;

  ${MOBILE} {
    padding: 4px 16px 16px;
  }
`

export const OriginItem = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px 12px;
  align-items: center;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const OriginName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.ink900};
`

export const OriginNumbers = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.ink600};
  text-align: right;

  ${MOBILE} {
    text-align: left;
  }
`

export const OriginBar = styled.div`
  grid-column: 1 / -1;
  display: flex;
`

export const HeatmapScroll = styled.div`
  overflow-x: auto;
  padding: 0 24px 12px;

  ${MOBILE} {
    padding: 0 12px 12px;
  }
`

export const Heatmap = styled.table`
  border-collapse: separate;
  border-spacing: 2px;
  font-size: 12px;
`

export const HeatHeader = styled.th`
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.ink500};
  white-space: nowrap;
  text-align: center;
`

export const HeatSector = styled.th`
  position: sticky;
  left: 0;
  z-index: 1;
  max-width: 180px;
  padding: 6px 10px 6px 0;
  background: ${colors.white};
  font-size: 12px;
  font-weight: 700;
  color: ${colors.ink800};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const HeatCell = styled.td<{ $intensity: number }>`
  min-width: 40px;
  height: 30px;
  border-radius: 4px;
  background: ${({ $intensity }) =>
    $intensity <= 0 ? colors.accent : `rgba(${colors.primaryRgb}, ${(0.12 + 0.88 * Math.min(1, $intensity)).toFixed(3)})`};
  color: ${({ $intensity }) => ($intensity > 0.55 ? colors.white : colors.ink800)};
  font-weight: 600;
  text-align: center;
`

export const HeatGap = styled.td`
  min-width: 18px;
  color: ${colors.ink400};
  text-align: center;
`

export const HeatTotal = styled.td`
  padding: 0 8px;
  font-weight: 800;
  color: ${colors.ink950};
  text-align: right;
`

export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px 16px;
  font-size: 12px;
  color: ${colors.ink500};
`

export const LegendRamp = styled.span`
  width: 120px;
  height: 8px;
  border-radius: ${radius.progress};
  background: linear-gradient(90deg, ${colors.accent}, ${colors.primary});
`

export const TableScroll = styled.div`
  overflow-x: auto;
  padding: 0 24px 20px;
`

export const Table = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-size: 14px;

  th {
    ${fieldLabel}
    padding: 10px;
    border-bottom: 1px solid ${colors.tableBorder};
    background: ${colors.zebra};
    text-align: right;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  td {
    padding: 12px 10px;
    border-bottom: 1px solid ${colors.rowDivider};
    color: ${colors.ink800};
    font-weight: 500;
    text-align: right;
    white-space: nowrap;
  }
`

export const SubTable = styled(Table)`
  min-width: 760px;
  font-size: 13px;
`

export const BarCell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
  min-width: 120px;
`

export const Status = styled.span<{ $state: 'on' | 'idle' | 'off' | 'end' }>`
  padding: 2px 10px;
  border-radius: ${radius.pill};
  font-size: 12px;
  font-weight: 700;
  ${({ $state }) =>
    $state === 'on'
      ? css`
          background: ${colors.positiveBg};
          color: ${colors.positive};
        `
      : $state === 'idle'
        ? css`
            background: ${colors.warningBg};
            color: ${colors.warningText};
          `
        : $state === 'off'
          ? css`
              background: ${colors.dangerBg};
              color: ${colors.dangerText};
            `
          : css`
              background: ${colors.accent};
              color: ${colors.ink600};
            `}
`

export const Pill = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  background: ${colors.dangerBg};
  color: ${colors.dangerText};
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`

export const Muted = styled.span`
  color: ${colors.ink500};
`

export const Attendance = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;

  & > span:last-child {
    flex-shrink: 0;
    min-width: 44px;
    font-weight: 700;
    text-align: right;
  }
`

export const DetailHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${colors.ink950};
`

export const Footnote = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: ${colors.ink500};
`

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`

export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 16px;

  @media print {
    display: none;
  }
`

export const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 16px 16px;
`

export const MobileGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export { ToggleAll } from '../../SalesReport/ReportContent/styles'

export const MobileCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.card};
  background: ${colors.white};
`

export const MobileCardButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

export const MobileCardHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`

export const MobileCardTitle = styled.span`
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${colors.ink950};
  overflow-wrap: anywhere;
`

export const MobileCardValue = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${colors.ink950};
`

export const MetricGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
  margin: 0;

  div {
    min-width: 0;
  }

  dt {
    ${fieldLabel}
  }

  dd {
    margin: 2px 0 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.ink800};
    overflow-wrap: anywhere;
  }
`

export const MobileDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid ${colors.rowDivider};
`

export const Hidden = styled.span`
  ${visuallyHidden}
`

export const Empty = styled.p`
  margin: 0;
  padding: 0 24px 20px;
  font-size: 13px;
  color: ${colors.ink500};
`

export const Gaps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const GapToggle = styled.button`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  align-self: flex-start;
  gap: 4px 10px;
  max-width: 100%;
  padding: 4px 12px;
  border: 0;
  border-radius: ${radius.pill};
  background: ${colors.warningBg};
  color: ${colors.warningText};
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`

export const GapAction = styled.span`
  font-weight: 700;
  text-decoration: underline;
  white-space: nowrap;
`

export const GapList = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 560px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.input};
`

export const GapRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px minmax(0, 1fr);
  gap: 12px;
  padding: 8px 12px;
  border-top: 1px solid ${colors.rowDivider};
  font-size: 13px;
  color: ${colors.ink800};

  &:first-child {
    border-top: 0;
  }

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr) auto;

    & > span:last-child {
      grid-column: 1 / -1;
    }
  }
`

export const GapDevice = styled.span`
  min-width: 0;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const GapMinutes = styled.span`
  font-weight: 700;
  color: ${colors.warningText};
  text-align: right;
`

export const GapWindow = styled.span`
  color: ${colors.ink500};
`

export const GapMore = styled.button`
  padding: 8px 12px;
  border: 0;
  border-top: 1px solid ${colors.rowDivider};
  background: transparent;
  color: ${colors.primaryStrong};
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: -2px;
  }
`
